import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InsuranceVerification = ({ verifications, onVerify, onViewDetails }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const getVerificationStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'bg-success/10 text-success border-success';
      case 'pending':
        return 'bg-warning/10 text-warning border-warning';
      case 'expired':
        return 'bg-destructive/10 text-destructive border-destructive';
      case 'failed':
        return 'bg-accent/10 text-accent border-accent';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getCoverageIcon = (type) => {
    switch (type) {
      case 'active':
        return 'CheckCircle';
      case 'inactive':
        return 'XCircle';
      case 'limited':
        return 'AlertCircle';
      default:
        return 'HelpCircle';
    }
  };

  const filteredVerifications = verifications?.filter(verification =>
    verification?.patientName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    verification?.insuranceProvider?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <h2 className="text-lg md:text-xl font-semibold text-foreground">Insurance Verification</h2>
          <Button
            variant="default"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={onVerify}
          >
            Verify All
          </Button>
        </div>

        <div className="relative">
          <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search verifications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-4 h-10 bg-muted border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>
      <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
        {filteredVerifications?.map((verification) => (
          <div key={verification?.id} className="p-4 md:p-6 hover:bg-muted/30 transition-smooth">
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-3 mb-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg flex-shrink-0">
                    <Icon name="Shield" size={20} color="var(--color-primary)" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground mb-1">{verification?.patientName}</h3>
                    <p className="text-sm text-muted-foreground">{verification?.insuranceProvider}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getVerificationStatusColor(verification?.status)} flex-shrink-0`}>
                    {verification?.status === 'verified' && <Icon name="CheckCircle" size={12} />}
                    {verification?.status === 'failed' && <Icon name="XCircle" size={12} />}
                    {verification?.status?.charAt(0)?.toUpperCase() + verification?.status?.slice(1)}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Policy Number</p>
                    <p className="text-sm font-mono text-foreground">{verification?.policyNumber}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Group Number</p>
                    <p className="text-sm font-mono text-foreground">{verification?.groupNumber}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Effective Date</p>
                    <p className="text-sm font-medium text-foreground">{verification?.effectiveDate}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg border border-border">
                    <Icon name={getCoverageIcon(verification?.coverageStatus)} size={16} className={
                      verification?.coverageStatus === 'active' ? 'text-success' :
                      verification?.coverageStatus === 'inactive'? 'text-destructive' : 'text-warning'
                    } />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground">Coverage</p>
                      <p className="text-sm font-medium text-foreground capitalize">{verification?.coverageStatus}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg border border-border">
                    <Icon name="DollarSign" size={16} className="text-primary" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground">Deductible</p>
                      <p className="text-sm font-medium text-foreground">${verification?.deductible?.toLocaleString('en-US')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg border border-border">
                    <Icon name="Percent" size={16} className="text-primary" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground">Copay</p>
                      <p className="text-sm font-medium text-foreground">${verification?.copay}</p>
                    </div>
                  </div>
                </div>

                {verification?.notes && (
                  <div className="mt-3 p-3 bg-muted/50 rounded-lg border border-border">
                    <p className="text-xs text-muted-foreground mb-1">Notes</p>
                    <p className="text-sm text-foreground">{verification?.notes}</p>
                  </div>
                )}
              </div>

              <div className="flex flex-row lg:flex-col gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="RefreshCw"
                  iconPosition="left"
                  onClick={() => onVerify(verification)}
                  className="flex-1 lg:flex-none"
                >
                  Re-verify
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Eye"
                  iconPosition="left"
                  onClick={() => onViewDetails(verification)}
                  className="flex-1 lg:flex-none"
                >
                  Details
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredVerifications?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <Icon name="Shield" size={48} className="text-muted-foreground mb-4" />
          <p className="text-foreground font-medium mb-1">No verifications found</p>
          <p className="text-sm text-muted-foreground">Try adjusting your search criteria</p>
        </div>
      )}
    </div>
  );
};

export default InsuranceVerification;